import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import { siteVisibility } from "@/config/siteVisibility";

// Lazy load non-critical pages
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const ConsultantsPage = lazy(() => import("./pages/ConsultantsPage"));
const ExpertisePage = lazy(() => import("./pages/ExpertisePage"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStoriesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ExpertiseFormPage = lazy(() => import("./pages/ExpertiseFormPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const VacaturesPage = lazy(() => import("./pages/VacaturesPage"));
const DataStudentPage = lazy(() => import("./pages/DataStudentPage"));
const NewDataSelectPage = lazy(() => import("./pages/NewDataSelectPage"));
const DutchDataCompanyPage = lazy(() => import("./pages/DutchDataCompanyPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CrystallineClusterPage = lazy(() => import("./pages/CrystallineClusterPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const AdminPostEditor = lazy(() => import("./pages/admin/AdminPostEditor"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              {siteVisibility.pages.companies !== false && <Route path="/bedrijven" element={<CompaniesPage />} />}
              {siteVisibility.pages.consultants !== false && <Route path="/consultants" element={<ConsultantsPage />} />}
              {siteVisibility.pages.expertise && <Route path="/expertise" element={<ExpertisePage />} />}
              {siteVisibility.pages.succesverhalen && <Route path="/succesverhalen" element={<SuccessStoriesPage />} />}
              {siteVisibility.pages.aboutUs && <Route path="/over-ons" element={<AboutPage />} />}
              
              <Route path="/expertise-formulier" element={<ExpertiseFormPage />} />
              <Route path="/datastudent" element={<DataStudentPage />} />
              <Route path="/new-data-select" element={<NewDataSelectPage />} />
              <Route path="/dutch-data-company" element={<DutchDataCompanyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/crystalline-cluster" element={<CrystallineClusterPage />} />
              <Route path="/algemene-voorwaarden" element={<TermsPage />} />
              
              {/* Blog routes */}
              {siteVisibility.pages.blog && <Route path="/blog" element={<BlogPage />} />}
              {siteVisibility.pages.blog && <Route path="/blog/:slug" element={<BlogPostPage />} />}
              {siteVisibility.pages.vacatures && <Route path="/vacatures" element={<VacaturesPage />} />}
              
              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/posts" element={<ProtectedRoute requireAdmin><AdminPosts /></ProtectedRoute>} />
              <Route path="/admin/posts/new" element={<ProtectedRoute requireAdmin><AdminPostEditor /></ProtectedRoute>} />
              <Route path="/admin/posts/:id/edit" element={<ProtectedRoute requireAdmin><AdminPostEditor /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute>} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
